import React, { useEffect, useState, useContext } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import { Refresh } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  dialogPaper: {
    minWidth: 320,
    maxWidth: 400,
    width: "100%",
    padding: theme.spacing(2),
    margin: 0,
    [theme.breakpoints.down("xs")]: {
      minWidth: "90vw",
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(3),
  },
  qrCodeContainer: {
    padding: theme.spacing(3),
    backgroundColor: "white",
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  loadingText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  refreshButton: {
    marginTop: theme.spacing(1),
  },
}));

const QrcodeModal = ({ open, onClose, whatsAppId, isAdmin }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const { user, socket } = useContext(AuthContext);

  // Fetch QR code via API (admin usa rota admin, normal usa rota normal)
  const fetchQrCode = async () => {
    if (!whatsAppId) return;
    setRefreshing(true);
    try {
      const endpoint = isAdmin
        ? `/whatsapp-admin/${whatsAppId}`
        : `/whatsapp/${whatsAppId}`;
      const { data } = await api.get(endpoint);
      if (data.status === "CONNECTED") {
        onClose();
        return;
      }
      setQrCode(data.qrcode || "");
    } catch (err) {
      toastError(err);
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch inicial quando abre o modal + auto-refresh após 5s no modo admin
  useEffect(() => {
    if (!open || !whatsAppId) return;
    let cancelled = false;

    // silent=true (polling) não mostra toast de erro — evita enxurrada de
    // toasts quando o plugin cai e cada tick falha. Só o fetch inicial avisa.
    const doFetch = async (silent = false) => {
      setRefreshing(true);
      try {
        const endpoint = isAdmin
          ? `/whatsapp-admin/${whatsAppId}`
          : `/whatsapp/${whatsAppId}`;
        const { data } = await api.get(endpoint);
        if (cancelled) return;
        if (data.status === "CONNECTED") {
          onClose();
          return;
        }
        // Conexão caiu (plugin desconectou / logout): não adianta esperar QR.
        // Fecha o modal em vez de travar em "waiting" com polling eterno.
        if (data.status === "DISCONNECTED") {
          onClose();
          return;
        }
        setQrCode(data.qrcode || "");
      } catch (err) {
        if (!cancelled && !silent) toastError(err);
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    };

    setQrCode("");
    doFetch();

    // Polling resiliente: o QR do WhatsApp expira/renova a cada ~20s e o plugin
    // reemite um novo. Não dependemos só do socket (que pode estar mudo por
    // sessão instável) — buscamos periodicamente para sempre ter o QR atual.
    // doFetch fecha o modal em CONNECTED ou DISCONNECTED.
    // Começa rápido (a sessão pode ainda estar gerando o 1º QR) e espaça depois.
    let attempts = 0;
    let interval;
    const tick = () => {
      if (cancelled) return;
      doFetch(true); // polling é silencioso
      attempts += 1;
      // 1.5s nas primeiras ~6 tentativas (QR inicial), depois 5s (renovação).
      const next = attempts < 6 ? 1500 : 5000;
      interval = setTimeout(tick, next);
    };
    interval = setTimeout(tick, 1500);

    return () => {
      cancelled = true;
      if (interval) clearTimeout(interval);
    };
  }, [open, whatsAppId, isAdmin]);

  // Modo normal: socket listener (superadmin não recebe eventos de outra empresa)
  useEffect(() => {
    if (isAdmin || !whatsAppId || !open) return;

    const onWhatsappData = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        setQrCode(data.session.qrcode);
      }

      if (data.action === "update" && data.session.qrcode === "") {
        onClose();
      }
    };
    socket.on(`company-${user.companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${user.companyId}-whatsappSession`, onWhatsappData);
    };
  }, [whatsAppId, onClose, user.companyId, isAdmin, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="qr-code-dialog"
    >
      <DialogContent className={classes.content}>
        <Paper elevation={0} className={classes.dialogPaper}>
          <Typography variant="h6" color="primary" gutterBottom>
            {i18n.t("qrCode.message")}
          </Typography>
          <div className={classes.root}>
            {qrCode ? (
              <div className={classes.qrCodeContainer}>
                <QRCode 
                  value={qrCode} 
                  size={280} 
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>
            ) : (
              <Typography variant="body1" className={classes.loadingText}>
                {i18n.t("qrCode.waiting")}
              </Typography>
            )}
          </div>
          {isAdmin && (
            <Tooltip title="Atualizar QR Code">
              <IconButton
                className={classes.refreshButton}
                onClick={fetchQrCode}
                disabled={refreshing}
              >
                {refreshing ? (
                  <CircularProgress size={20} />
                ) : (
                  <Refresh />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);